import { atom } from "jotai";
import { Accounting } from "@/core/entities/lookup/accounting";
import { Purchaser } from "@/core/entities/lookup/purchaser";
import { Retailer } from "@/core/entities/lookup/retailer";
import { DistributionCenter } from "@/core/entities/lookup/distributionCenter";
import { Priority } from "@/core/entities/lookup/priority";
import { Validity } from "@/core/entities/lookup/validity";
import { DeductionType } from "@/core/entities/lookup/deductionType";
import { Stage } from "@/core/entities/lookup/stage";
import { GLCode } from "@/core/entities/lookup/glCode";
import { DeductionDocumentSubType, DocumentType } from "@/core/entities/lookup/documentType";
import { BrandMember } from "@/core/entities/lookup/brandMember";
import { AdjustmentType, AdjustmentCategories, DeductionTypeCategory } from "@/core/entities/lookup";
import { PromoType } from "@/core/entities/lookup/promoType";
import { PromoStatus } from "@/core/entities/lookup/promoStatus";
import { DisputeTrackingStatus } from "@/core/entities/lookup/disputeTrackingStatus";
import { ProductLookUp } from "@/core/entities/lookup/product";

interface Lookup<T> {
  data: T[];
  count: number;
  isLoading: boolean;
  error: string | null;
}

export interface LookupState {
  purchasers: Lookup<Purchaser>;
  retailers: Lookup<Retailer>;
  distributionCenters: Lookup<DistributionCenter>;
  priorities: Lookup<Priority>;
  validities: Lookup<Validity>;
  stages: Lookup<Stage>;
  deductionTypes: Lookup<DeductionType>;
  glCodes: Lookup<GLCode>;
  documentTypes: Lookup<DocumentType>;
  brandMembers: Lookup<BrandMember>;
  adjustmentTypes: Lookup<AdjustmentType>;
  adjustmentCategories: Lookup<AdjustmentCategories>;
  deductionTypeCategories: Lookup<DeductionTypeCategory>;
  documentSubTypes: Lookup<DeductionDocumentSubType>;
  accountingSystems: Lookup<Accounting>;
  promoTypes: Lookup<PromoType>;
  promoStatuses: Lookup<PromoStatus>;
  disputeTrackingStatuses: Lookup<DisputeTrackingStatus>;
  products: Lookup<ProductLookUp>;
}

const initialState: LookupState = {
  purchasers: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  retailers: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  distributionCenters: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  priorities: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  validities: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  stages: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  deductionTypes: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  glCodes: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  documentTypes: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  brandMembers: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  adjustmentTypes: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  adjustmentCategories: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  deductionTypeCategories: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  documentSubTypes: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  accountingSystems: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  promoTypes: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  promoStatuses: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  disputeTrackingStatuses: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
  products: {
    data: [],
    count: 0,
    isLoading: false,
    error: null,
  },
};

// Base atoms
export const lookupAtom = atom<LookupState>(initialState);

// Derived atoms for specific states
export const purchasersAtom = atom(get => get(lookupAtom).purchasers);
export const retailersAtom = atom(get => get(lookupAtom).retailers);
export const distributionCentersAtom = atom(get => get(lookupAtom).distributionCenters);
export const prioritiesAtom = atom(get => get(lookupAtom).priorities);
export const validitiesAtom = atom(get => get(lookupAtom).validities);
export const stagesAtom = atom(get => get(lookupAtom).stages);
export const deductionTypesAtom = atom(get => get(lookupAtom).deductionTypes);
export const glCodesAtom = atom(get => get(lookupAtom).glCodes);
export const documentTypesAtom = atom(get => get(lookupAtom).documentTypes);
export const brandMembersAtom = atom(get => get(lookupAtom).brandMembers);
export const adjustmentTypesAtom = atom(get => get(lookupAtom).adjustmentTypes);
export const adjustmentCategoriesAtom = atom(get => get(lookupAtom).adjustmentCategories);
export const deductionTypeCategoriesAtom = atom(get => get(lookupAtom).deductionTypeCategories);
export const documentSubTypesAtom = atom(get => get(lookupAtom).documentSubTypes);
export const accountingSystemsAtom = atom(get => get(lookupAtom).accountingSystems);
export const promoTypesAtom = atom(get => get(lookupAtom).promoTypes);
export const promoStatusesAtom = atom(get => get(lookupAtom).promoStatuses);
export const disputeTrackingStatusesAtom = atom(get => get(lookupAtom).disputeTrackingStatuses);
export const productsAtom = atom(get => get(lookupAtom).products);
